import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useQuery, useInfiniteQuery } from "react-query";
import { toast } from "react-toastify";
import { getSalesComparisonData } from "../../services/analyticsService";
import { getCommonProducts } from "../../services/commonProductService";
import { formatAmount } from "../../utils/formatters";

const toYMD = (date) => new Date(date).toLocaleDateString("en-CA");

// Static platform options (same as ProductAnalytics)
const PLATFORM_OPTIONS = [
  { value: "all", label: "All Platforms" },
  { value: "amazon", label: "Amazon" },
  { value: "flipkart", label: "Flipkart" },
  { value: "shopify", label: "Shopify" },
  { value: "tata 1mg", label: "Tata 1mg" },
  { value: "palsons derma pvt ltd", label: "Palsons Derma Pvt Ltd" },
];

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const SalesComparison = () => {
  // Ensure a portal root exists for datepicker popper so it doesn't overflow/clip within cards
  useEffect(() => {
    const portalId = "datepicker-portal";
    let el = document.getElementById(portalId);
    if (!el) {
      el = document.createElement("div");
      el.id = portalId;
      el.style.position = "relative";
      el.style.zIndex = "9999";
      document.body.appendChild(el);
    }
  }, []);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const STORAGE_KEY = "filters:sales-comparison";
  let savedFilters = null;
  try {
    savedFilters = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch (_) {}
  const TODAY_KEY = new Date().toLocaleDateString("en-CA");
  if (savedFilters && savedFilters.dateKey !== TODAY_KEY) {
    savedFilters = null;
  }

  // Default: Base = last 7 days, Compare = previous 7 days
  const [baseEnd, setBaseEnd] = useState(() =>
    savedFilters?.baseEnd ? new Date(savedFilters.baseEnd) : today
  );
  const [baseStart, setBaseStart] = useState(() => {
    if (savedFilters?.baseStart) return new Date(savedFilters.baseStart);
    const d = new Date(baseEnd);
    d.setDate(d.getDate() - 6);
    return d;
  });
  const [compareEnd, setCompareEnd] = useState(() => {
    if (savedFilters?.compareEnd) return new Date(savedFilters.compareEnd);
    const d = new Date(baseStart);
    d.setDate(d.getDate() - 1);
    return d;
  });
  const [compareStart, setCompareStart] = useState(() => {
    if (savedFilters?.compareStart) return new Date(savedFilters.compareStart);
    const d = new Date(compareEnd);
    d.setDate(d.getDate() - 6);
    return d;
  });

  const MAX_RANGE_DAYS = 90; // inclusive

  // Dynamic min/max limits to enforce 90-day max range
  const baseStartMin = useMemo(
    () => addDays(baseEnd, -(MAX_RANGE_DAYS - 1)),
    [baseEnd]
  );
  const baseEndMax = useMemo(() => {
    const limit = addDays(baseStart, MAX_RANGE_DAYS - 1);
    return limit > today ? today : limit;
  }, [baseStart, today]);

  const compareStartMin = useMemo(
    () => addDays(compareEnd, -(MAX_RANGE_DAYS - 1)),
    [compareEnd]
  );
  const compareEndMax = useMemo(() => {
    const limit = addDays(compareStart, MAX_RANGE_DAYS - 1);
    return limit > today ? today : limit;
  }, [compareStart, today]);

  // State declarations must come before useQuery
  const [didInitFetch, setDidInitFetch] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [selectedProductSku, setSelectedProductSku] = useState("");
  const [selectedProductName, setSelectedProductName] = useState("");
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const productDropdownRef = React.useRef(null);

  // Query for filtered data (for charts and display)
  const { data, isLoading, refetch } = useQuery(
    [
      "sales-comparison",
      toYMD(baseStart),
      toYMD(baseEnd),
      toYMD(compareStart),
      toYMD(compareEnd),
      selectedPlatform,
      selectedProductSku,
    ],
    async () =>
      getSalesComparisonData({
        baseStart: toYMD(baseStart),
        baseEnd: toYMD(baseEnd),
        compareStart: toYMD(compareStart),
        compareEnd: toYMD(compareEnd),
        platform: selectedPlatform,
        productSku: selectedProductSku.trim() || undefined,
      }),
    {
      enabled: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      onError: (err) => {
        toast.error(err?.message || "Failed to load comparison data");
      },
    }
  );

  const comparison = React.useMemo(() => data?.data ?? data, [data]);
  // Product options: same API as Manage common products (getCommonProducts with searchKey). First 10 on open, next 10 on scroll, ordered by name.
  const searchQueryForApi = productSearchQuery.trim();
  const PAGE_SIZE = 10;
  const {
    data: productOptionsInfiniteData,
    isLoading: productOptionsLoading,
    isFetchingNextPage,
    hasNextPage: productOptionsHasNextPage,
    fetchNextPage: fetchNextProductPage,
  } = useInfiniteQuery(
    ["commonProducts-search", searchQueryForApi],
    ({ pageParam = 1 }) =>
      getCommonProducts({
        page: pageParam,
        limit: PAGE_SIZE,
        sortField: "name",
        sortValue: "ASC",
        searchKey: searchQueryForApi,
      }),
    {
      enabled: productSearchOpen,
      getNextPageParam: (lastPage, allPages) =>
        lastPage?.data?.hasNext ? (allPages?.length ?? 0) + 1 : undefined,
      staleTime: 60 * 1000,
    }
  );
  const productOptionsFromApi = useMemo(() => {
    const pages = productOptionsInfiniteData?.pages ?? [];
    return pages.flatMap((page) => {
      const list = Array.isArray(page?.data?.data) ? page.data.data : [];
      return list
        .filter((p) => p.baseSku)
        .map((p) => ({ product: p.name || "", sku: p.baseSku || "" }));
    });
  }, [productOptionsInfiniteData?.pages]);

  useEffect(() => {
    (async () => {
      await refetch();
      setDidInitFetch(true);
    })();
  }, [refetch]);

  // Refetch when date values change
  useEffect(() => {
    if (!didInitFetch) return;
    refetch();
  }, [baseStart, baseEnd, compareStart, compareEnd, didInitFetch, refetch]);

  // Refetch when platform or product (SKU) selection changes (only filtered data)
  useEffect(() => {
    if (!didInitFetch) return;
    refetch();
  }, [selectedPlatform, selectedProductSku, didInitFetch, refetch]);

  // Persist filters
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          dateKey: TODAY_KEY,
          baseStart,
          baseEnd,
          compareStart,
          compareEnd,
        })
      );
    } catch (_) {}
  }, [baseStart, baseEnd, compareStart, compareEnd]);

  // Product options: "All Products" + results from common products (same search as Manage common products)
  const productOptions = useMemo(
    () => [{ product: "All Products", sku: "" }, ...productOptionsFromApi],
    [productOptionsFromApi]
  );

  const selectedProductLabel = useMemo(() => {
    if (!selectedProductSku) return "All Products";
    return selectedProductName || selectedProductSku;
  }, [selectedProductSku, selectedProductName]);

  // Filter platform data based on selection
  const platformData = useMemo(() => {
    const allPlatforms = (comparison?.platform_wise || []).map((p) => ({
      name: p.platform,
      base: Number(p.base || 0),
      compare: Number(p.compare || 0),
    }));
    
    if (selectedPlatform === "all") {
      return allPlatforms;
    }
    
    return allPlatforms.filter(p => p.name === selectedPlatform);
  }, [comparison, selectedPlatform]);

  const zoneData = useMemo(() => {
    return (comparison?.zone_wise || []).map((z) => ({
      name: z.zone,
      base: Number(z.base || 0),
      compare: Number(z.compare || 0),
    }));
  }, [comparison]);

  const stateData = useMemo(() => {
    return (comparison?.state_wise || [])
      .filter((s) => s.state && s.state.toLowerCase() !== 'n/a' && s.state !== 'N/A')
      .map((s) => ({
        name: s.state,
        shortName: s.state?.length > 20 ? `${s.state.slice(0, 20)}...` : s.state,
        base: Number(s.base || 0),
        compare: Number(s.compare || 0),
      }));
  }, [comparison]);

  const productData = useMemo(() => {
    return (comparison?.product_wise || []).map((p) => ({
      name: p.product,
      shortName:
        p.product?.length > 20 ? `${p.product.slice(0, 20)}...` : p.product,
      base: Number(p.base || 0),
      compare: Number(p.compare || 0),
    }));
  }, [comparison]);

  const stateChartWidth = useMemo(() => {
    const count = stateData.length || 1;
    // Aim ~70px per category to make grouped bars visually wider
    return Math.max(900, count * 70);
  }, [stateData]);

  const productChartWidth = useMemo(() => {
    const count = productData.length || 1;
    // Products need more space due to longer names
    return Math.max(1000, count * 120);
  }, [productData]);

  // Label renderers
  const renderBarLabelBase = ({ x, y, width, height, value }) => {
    if (value == null) return null;
    const label = formatAmount(value);

    console.log(x, y, width, height, value);  
    
    // Check if bar is too small to show label inside (less than 30px width)
    const isBarTooSmall = (width || 0) < 30;

    console.log('isBarTooSmall: ',isBarTooSmall);

    return (
        <text
        x={x + width / 2}
        y={isBarTooSmall ? y - 5 : y + height / 2}
          textAnchor="middle"
        dominantBaseline={isBarTooSmall ? "baseline" : "middle"}
        fill={isBarTooSmall ? "#6b7280" : "white"}
        fontSize="12"
        fontWeight="bold"
        transform={isBarTooSmall ? "" : `rotate(-90, ${x + width / 2}, ${y + height / 2})`}
        >
        {value}
        </text>
    );
    
    // return (
    //   <text
    //     x={x + width / 2}
    //     y={isBarTooSmall ? y - 5 : y + height / 2}
    //     textAnchor="middle"
    //     dominantBaseline={isBarTooSmall ? "baseline" : "middle"}
    //     fill={isBarTooSmall ? "black" : "white"}
    //     fontSize="12"
    //     fontWeight="bold"
    //     transform={isBarTooSmall ? "" : `rotate(-90, ${x + width / 2}, ${y + height / 2})`}
    //   >
    //     {label}
    //   </text>
    // );
  };

 

  const renderBarLabel = ({ x, y, width, height, value }) => {
    if (value == null) return null;
    const label = formatAmount(value);
    // const labelWidth = label.length * 7 + 8;
    // const labelHeight = 14;
    // const centerX = x + width / 2;
    // const centerY = y - 30; // slightly more gap above bar top
    // return (
    //   <g transform={`rotate(-90, ${centerX}, ${centerY})`}>
    //     <rect
    //       x={centerX - labelWidth / 2}
    //       y={centerY - labelHeight / 2}
    //       width={labelWidth}
    //       height={labelHeight}
    //       rx={3}
    //       ry={3}
    //       fill="rgba(255,255,255,0.9)"
    //       stroke="#e5e7eb"
    //     />
    //     <text
    //       x={centerX}
    //       y={centerY}
    //       fill="#374151"
    //       fontSize={10}
    //       textAnchor="middle"
    //       dominantBaseline="central"
    //     >
    //       {label}
    //     </text>
    //   </g>
    // );

      
      // If bar is too small (less than 30px height), show label on top
      const isBarTooSmall = height < 30;
      
    return (
        <text
          x={isBarTooSmall ? x + width +26 : x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline={isBarTooSmall ? "middle" : "middle"}
          fill={isBarTooSmall ? "#6b7280" : "white"}
          fontSize="12"
          fontWeight="bold"
          transform={`rotate(-90, ${x + width / 2}, ${y + height / 2})`}
        >
          {label}
        </text>
      );
    
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-4 h-full">
          <div className="font-semibold mb-3 text-sm text-gray-700">Base range</div>
          <div className="flex gap-2">
            <DatePicker
              selected={baseStart}
              onChange={(d) => setBaseStart(d)}
              className="input input-bordered w-full text-sm"
              dateFormat="yyyy-MM-dd"
              maxDate={baseEnd}
              minDate={baseStartMin}
              popperPlacement="bottom-start"
              portalId="datepicker-portal"
              popperClassName="z-[9999] text-xs"
              calendarClassName="text-xs"
              dayClassName={() => "text-xs"}
            />
            <DatePicker
              selected={baseEnd}
              onChange={(d) => setBaseEnd(d)}
              className="input input-bordered w-full text-sm"
              dateFormat="yyyy-MM-dd"
              minDate={baseStart}
              maxDate={baseEndMax}
              popperPlacement="bottom-start"
              portalId="datepicker-portal"
              popperClassName="z-[9999] text-xs"
              calendarClassName="text-xs"
              dayClassName={() => "text-xs"}
            />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 h-full">
          <div className="font-semibold mb-3 text-sm text-gray-700">Compare range</div>
          <div className="flex gap-2">
            <DatePicker
              selected={compareStart}
              onChange={(d) => setCompareStart(d)}
              className="input input-bordered w-full text-sm"
              dateFormat="yyyy-MM-dd"
              maxDate={compareEnd}
              minDate={compareStartMin}
              popperPlacement="bottom-start"
              portalId="datepicker-portal"
              popperClassName="z-[9999] text-xs"
              calendarClassName="text-xs"
              dayClassName={() => "text-xs"}
            />
            <DatePicker
              selected={compareEnd}
              onChange={(d) => setCompareEnd(d)}
              className="input input-bordered w-full text-sm"
              dateFormat="yyyy-MM-dd"
              minDate={compareStart}
              maxDate={compareEndMax}
              popperPlacement="bottom-start"
              portalId="datepicker-portal"
              popperClassName="z-[9999] text-xs"
              calendarClassName="text-xs"
              dayClassName={() => "text-xs"}
            />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 h-full flex flex-col justify-between">
          <div className="text-sm text-gray-500 mb-1">Net Sales</div>
          <div className="flex gap-8">
            <div>
              <div className="text-xs uppercase text-gray-400">Base</div>
              <div className="text-md font-bold text-emerald-700">
                {formatAmount(comparison?.base_net_sales || 0)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-400">Compare</div>
              <div className="text-md font-bold text-emerald-400">
                {formatAmount(comparison?.compare_net_sales || 0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className={`bg-white rounded-lg shadow p-4 h-full relative ${productSearchOpen ? "z-[10001]" : ""}`}>
          <div className="font-semibold mb-3 text-sm text-gray-700">Product</div>
          <div ref={productDropdownRef} className="relative">
            <input
              type="text"
              value={productSearchOpen ? productSearchQuery : selectedProductLabel}
              onChange={(e) => {
                setProductSearchQuery(e.target.value);
                setProductSearchOpen(true);
              }}
              onFocus={() => {
                setProductSearchOpen(true);
                setProductSearchQuery("");
              }}
              onClick={() => {
                setProductSearchOpen(true);
                setProductSearchQuery("");
              }}
              onBlur={() => {
                setTimeout(() => {
                  if (productDropdownRef.current && !productDropdownRef.current.contains(document.activeElement)) {
                    setProductSearchOpen(false);
                  }
                }, 200);
              }}
              placeholder="Search product or SKU..."
              className="input input-bordered w-full pr-8"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
              {productSearchOpen ? "⌃" : "⌄"}
            </span>
            {productSearchOpen && (
              <ul
                className="absolute z-[10001] mt-1 w-full max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg py-1 text-sm"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  if (!productOptionsHasNextPage || isFetchingNextPage) return;
                  const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
                  if (nearBottom) fetchNextProductPage();
                }}
              >
                {productOptionsLoading && productOptionsFromApi.length === 0 ? (
                  <li className="px-3 py-2 text-gray-500">{productSearchQuery.trim() ? "Searching..." : "Loading..."}</li>
                ) : productOptions.length <= 1 ? (
                  <li className="px-3 py-2 text-gray-500">{productSearchQuery.trim() ? "No matches" : "Type to search or scroll for more"}</li>
                ) : (
                  <>
                    {productOptions.map((opt) => (
                      <li
                        key={opt.sku ? `sku-${opt.sku}` : "all"}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 flex flex-col"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedProductSku(opt.sku || "");
                          setSelectedProductName(opt.product || "");
                          setProductSearchQuery("");
                          setProductSearchOpen(false);
                        }}
                      >
                        <span className="font-medium truncate" title={opt.product}>
                          {opt.product || "All Products"}
                        </span>
                        {opt.sku && (
                          <span className="text-xs text-gray-500 truncate">SKU: {opt.sku}</span>
                        )}
                      </li>
                    ))}
                    {productOptionsHasNextPage && (
                      <li className="px-3 py-2 text-gray-500 text-center border-t border-gray-100">
                        {isFetchingNextPage ? "Loading more..." : "Scroll for more"}
                      </li>
                    )}
                  </>
                )}
              </ul>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">Selection filters orders by that product’s SKU</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 h-full">
          <div className="font-semibold mb-3 text-sm text-gray-700">Platform Filter</div>
          <select
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
            className="select select-bordered w-full"
          >
            {PLATFORM_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Top charts: Left = Platform line, Right = Zone bar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Platform wise</h3>
          <div className="relative z-[9999] overflow-visible">
            <ResponsiveContainer width="100%" height={360}>
              <BarChart
                data={platformData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                />
                <YAxis 
                  tick={{ fontSize: 14, fill: "#6b7280" }}
                  tickFormatter={(value) => formatAmount(value)}
                />
                <Tooltip
                  formatter={(v) => formatAmount(v)}
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    maxWidth: 280,
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                  wrapperStyle={{ maxWidth: 280 }}
                  labelStyle={{
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                />
                <Legend />
                {/* Base Bar */}
                <Bar
                  dataKey="base"
                  name="Base"
                  fill="#dc2626"
                  radius={[4, 4, 0, 0]}
                  barSize={30}
                >
                  {platformData.length <= 8 && (
                    <LabelList  content={(props) => {
                      const { x, y, width, height, value } = props;
                      if (!value) return null;

                      const label = formatAmount(value);
                      
                      // If bar is too small (less than 30px height), show label on top
                      const isBarTooSmall = height < 60;
                      
                      return (
                        <text
                          x={isBarTooSmall ? x + width +26 : x + width / 2}
                          y={y + height / 2}
                          textAnchor="middle"
                          dominantBaseline={isBarTooSmall ? "middle" : "middle"}
                          fill={isBarTooSmall ? "#6b7280" : "white"}
                          fontSize="12"
                          fontWeight="bold"
                          transform={`rotate(-90, ${x + width / 2}, ${y + height / 2})`}
                        >
                          {label}
                        </text>
                      );
                    }} />
                  )}
                </Bar>
                {/* Compare Bar */}
                <Bar
                  dataKey="compare"
                  name="Compare"
                  fill="#1e40af"
                  radius={[4, 4, 0, 0]}
                  barSize={30}
                >
                  {platformData.length <= 8 && (
                    <LabelList content={renderBarLabel}  />
                  )}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Zone wise</h3>
          <div className="relative z-[9999] overflow-visible">
            <ResponsiveContainer width="100%" height={360}>
              <BarChart
                data={zoneData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                />
                <YAxis 
                  tick={{ fontSize: 14, fill: "#6b7280" }}
                  tickFormatter={(value) => formatAmount(value)}
                />
                <Tooltip
                  formatter={(v) => formatAmount(v)}
                  cursor={{ fill: "rgba(148,163,184,0.15)" }}
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    maxWidth: 280,
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                  wrapperStyle={{ maxWidth: 280 }}
                  labelStyle={{
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                />
                <Legend />
                {/* Orange family */}
                <Bar
                  dataKey="base"
                  name="Base"
                  fill="#059669"
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList content={renderBarLabel} />
                </Bar>
                <Bar
                  dataKey="compare"
                  name="Compare"
                  fill="#7c3aed"
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList content={renderBarLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* State wise bar chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">State wise</h3>
        <div className="overflow-x-auto">
          <div
            style={{ width: stateChartWidth }}
            className="relative z-[9999] overflow-visible"
          >
            <BarChart
              width={stateChartWidth}
              height={460}
              data={stateData}
              barCategoryGap="30%"
              barGap={8}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="shortName"
                tick={{ fontSize: 12, fill: "#6b7280" }}
                interval={0}
                angle={-30}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tick={{ fontSize: 14, fill: "#6b7280" }}
                tickFormatter={(value) => formatAmount(value)}
              />
              <Tooltip
                formatter={(v) => formatAmount(v)}
                cursor={{ fill: "rgba(148,163,184,0.15)" }}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  maxWidth: 280,
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
                wrapperStyle={{ maxWidth: 280 }}
                labelStyle={{
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
              />
              <Legend />
              {/* Purple family */}
              <Bar
                dataKey="base"
                name="Base"
                fill="#ea580c"
                radius={[4, 4, 0, 0]}
                barSize={36}
              >
                <LabelList content={renderBarLabel} />
              </Bar>
              <Bar
                dataKey="compare"
                name="Compare"
                fill="#0891b2"
                radius={[4, 4, 0, 0]}
                barSize={36}
              >
                <LabelList content={renderBarLabel} />
              </Bar>
            </BarChart>
          </div>
        </div>
      </div>

      {/* Product wise bar chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Product wise</h3>
        <div className="overflow-x-auto">
          <div
            style={{ width: productChartWidth }}
            className="relative z-[9999] overflow-visible"
          >
            <BarChart
              width={productChartWidth}
              height={500}
              data={productData}
              barCategoryGap="20%"
              barGap={8}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="shortName"
                tick={{ fontSize: 10, fill: "#6b7280" }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={120}
              />
              <YAxis 
                tick={{ fontSize: 14, fill: "#6b7280" }}
                tickFormatter={(value) => formatAmount(value)}
              />
              <Tooltip
                formatter={(v) => formatAmount(v)}
                labelFormatter={(label, payload) => {
                  // Show full product name in tooltip
                  const item = payload?.[0]?.payload;
                  return item?.name || label;
                }}
                cursor={{ fill: "rgba(148,163,184,0.15)" }}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  maxWidth: 320,
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
                wrapperStyle={{ maxWidth: 320 }}
                labelStyle={{
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
              />
              <Legend />
              {/* Teal family */}
              <Bar
                dataKey="base"
                name="Base"
                fill="#db2777"
                radius={[4, 4, 0, 0]}
                barSize={40}
              >
                <LabelList content={renderBarLabel} />
              </Bar>
              <Bar
                dataKey="compare"
                name="Compare"
                fill="#4f46e5"
                radius={[4, 4, 0, 0]}
                barSize={40}
              >
                <LabelList content={renderBarLabel} />
              </Bar>
            </BarChart>
          </div>
        </div>
      </div>

      {/* Product wise table */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Product Comparison Table</h3>
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th className="text-left font-semibold">Product Name</th>
                <th className="text-center font-semibold">Base Period</th>
                <th className="text-center font-semibold">Compare Period</th>
                <th className="text-center font-semibold">Difference</th>
                <th className="text-center font-semibold">% Change</th>
                <th className="text-center font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {productData.map((product, index) => {
                const difference = product.compare - product.base;
                const percentChange = product.base > 0 
                  ? ((difference / product.base) * 100).toFixed(1)
                  : product.compare > 0 ? 100 : 0;
                
                const isPositive = difference > 0;
                const isSignificant = Math.abs(percentChange) >= 10;
                
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="font-medium">
                      <div className="max-w-xs">
                        <div className="truncate" title={product.name}>
                          {product.name}
                        </div>
                      </div>
                    </td>
                    <td className="text-center font-mono">
                      {formatAmount(product.base)}
                    </td>
                    <td className="text-center font-mono">
                      {formatAmount(product.compare)}
                    </td>
                    <td className={`text-center font-mono ${
                      isPositive ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {isPositive ? '+' : ''}{formatAmount(difference)}
                    </td>
                    <td className={`text-center font-mono ${
                      isPositive ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {isPositive ? '+' : ''}{percentChange}%
                    </td>
                    <td className="text-center">
                      <span className={`badge badge-sm ${
                        isPositive 
                          ? 'badge-success' 
                          : 'badge-error'
                      } ${isSignificant ? 'badge-outline' : ''}`}>
                        {isPositive ? '↗' : '↘'} {isSignificant ? 'Significant' : 'Minor'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-bold bg-gray-100">
                <td>Total</td>
                <td className="text-center font-mono">
                  {formatAmount(productData.reduce((sum, p) => sum + p.base, 0))}
                </td>
                <td className="text-center font-mono">
                  {formatAmount(productData.reduce((sum, p) => sum + p.compare, 0))}
                </td>
                <td className={`text-center font-mono ${
                  productData.reduce((sum, p) => sum + (p.compare - p.base), 0) > 0 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {productData.reduce((sum, p) => sum + (p.compare - p.base), 0) > 0 ? '+' : ''}
                  {formatAmount(productData.reduce((sum, p) => sum + (p.compare - p.base), 0))}
                </td>
                <td className={`text-center font-mono ${
                  productData.reduce((sum, p) => sum + p.base, 0) > 0 
                    ? (productData.reduce((sum, p) => sum + (p.compare - p.base), 0) > 0 ? 'text-green-600' : 'text-red-600')
                    : ''
                }`}>
                  {productData.reduce((sum, p) => sum + p.base, 0) > 0 
                    ? (productData.reduce((sum, p) => sum + (p.compare - p.base), 0) > 0 ? '+' : '')
                    : ''}
                  {productData.reduce((sum, p) => sum + p.base, 0) > 0 
                    ? (((productData.reduce((sum, p) => sum + (p.compare - p.base), 0) / productData.reduce((sum, p) => sum + p.base, 0)) * 100)).toFixed(1)
                    : 0}%
                </td>
                <td className="text-center">
                  <span className={`badge badge-sm ${
                    productData.reduce((sum, p) => sum + (p.compare - p.base), 0) > 0 
                      ? 'badge-success' 
                      : 'badge-error'
                  }`}>
                    {productData.reduce((sum, p) => sum + (p.compare - p.base), 0) > 0 ? '↗ Growth' : '↘ Decline'}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      
    </div>
  );
};

export default SalesComparison;
